/**
 * Optimistic updates for board cards: applies an edit made in the issue side panel to one card.
 * The same path folds the confirmed edit into the cached board.
 *
 * The edit is in the issue viewer's GraphQL shape and the card is in the board payload's shape,
 * so each kind of edit has its own translation:
 * - `setFields` (title, state, labels, assignees, ...) in `fields.ts`
 * - a custom issue field's value in `custom-issue-fields.ts`
 * - a parent's sub-issue progress, when a sub-issue is added, removed, closed or reopened, in
 *   `sub-issues.ts`
 *
 * Returns the same card when nothing applies, so a card the edit doesn't change keeps its identity.
 */

import type {FieldDefinition} from '../../../saved-view-options/fields'
import type {BoardItemEdit} from '../../board-document-adapter'
import type {BoardPayloadItem} from '../board-payload'
import {applyCustomFieldEditToCard} from './custom-issue-fields'
import {applyFieldsEditToCard} from './fields'
import {applySubIssuesEditToCard} from './sub-issues'

export function applyOptimisticUpdateToCard(
  item: BoardPayloadItem,
  edit: BoardItemEdit,
  fieldDefinitions: readonly FieldDefinition[],
): BoardPayloadItem {
  switch (edit.type) {
    case 'setFields':
      return applyFieldsEditToCard(item, edit.fields)
    case 'upsertEntityFieldItem':
    case 'removeEntityFieldItem':
      return applyCustomFieldEditToCard(item, edit, fieldDefinitions)
    case 'updateEntityFieldItems':
    case 'deltaEntityFieldValues':
      return applySubIssuesEditToCard(item, edit)
  }
}
